import { Component } from '@theme/component';
import { QuoteList } from '@theme/quote-list';

/**
 * "Add to Quote" button for `quote_only` products.
 *
 * Sits inside the existing `<product-form-component>` (see blocks/buy-buttons.liquid) so it
 * can read the currently selected variant id and quantity. On click it enriches the item
 * with variant data (title/sku/image) by fetching the product's `.js` endpoint, then adds
 * it to the separate quote list — it never posts to the Shopify cart.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} addToQuoteButton
 *
 * @extends {Component<Refs>}
 */
class AddToQuoteComponent extends Component {
  requiredRefs = ['addToQuoteButton'];

  /** @returns {HTMLFormElement | null} The enclosing product form, if any. */
  get #form() {
    return this.closest('form') ?? this.closest('product-form-component')?.querySelector('form') ?? null;
  }

  /**
   * Handles the add-to-quote click.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const button = this.refs.addToQuoteButton;
    if (button.disabled) return;

    const form = this.#form;
    const variantId = form?.querySelector('[name="id"]')?.value || this.dataset.variantId;
    if (!variantId) return;

    const quantityValue = form?.querySelector('[name="quantity"]')?.value;
    const quantity = Math.max(1, parseInt(quantityValue ?? '1', 10) || 1);
    const productUrl = this.dataset.productUrl || '';

    /** @type {import('@theme/quote-list').QuoteItem} */
    const item = {
      id: String(variantId),
      productId: this.dataset.productId || '',
      title: this.dataset.productTitle || '',
      url: productUrl,
      quantity,
    };

    // Enrich with live variant data so the quote captures the exact variant + SKU.
    try {
      const response = await fetch(`${productUrl}.js`);
      if (response.ok) {
        const product = await response.json();
        const variant =
          product.variants?.find((entry) => String(entry.id) === String(variantId)) ?? product.variants?.[0];

        item.title = product.title || item.title;
        if (variant) {
          item.variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
          item.sku = variant.sku || '';
          item.image = variant.featured_image?.src || product.featured_image || '';
          item.url = `${productUrl}?variant=${variant.id}`;
        }
      }
    } catch (_) {
      // Network/parse failure — fall back to the minimal item built above.
    }

    QuoteList.add(item);
    this.#showAddedFeedback();
  };

  /** Brief visual confirmation, reusing the theme's add-to-cart "added" state. */
  #showAddedFeedback() {
    const button = this.refs.addToQuoteButton;
    button.classList.add('atc-added', 'add-to-cart__added-active');
    button.setAttribute('aria-disabled', 'true');

    clearTimeout(this.#feedbackTimer);
    this.#feedbackTimer = setTimeout(() => {
      button.classList.remove('atc-added', 'add-to-cart__added-active');
      button.removeAttribute('aria-disabled');
    }, 1500);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #feedbackTimer;
}

if (!customElements.get('add-to-quote-component')) {
  customElements.define('add-to-quote-component', AddToQuoteComponent);
}
